import React from 'react';
import dashboardService from '../../services/dashboardService';

const DashboardHealthCards = ({ metrics = {} }) => {
  const totalSnaps = metrics.totalSnaps || 0;
  const approvedSnaps = metrics.approvedSnaps || 0;
  const pendingSnaps = metrics.pendingSnaps || 0;
  const rejectedSnaps = metrics.rejectedSnaps || 0;

  const approvedPct = totalSnaps > 0 ? Math.round((approvedSnaps / totalSnaps) * 100) : 0;
  const pendingPct = totalSnaps > 0 ? Math.round((pendingSnaps / totalSnaps) * 100) : 0;
  const rejectedPct = totalSnaps > 0 ? Math.round((rejectedSnaps / totalSnaps) * 100) : 0;

  const totalMerchants = metrics.totalMerchants || 0;
  const activeMerchants = metrics.activeMerchants || 0;
  const activeMerchantPct = totalMerchants > 0 ? Math.round((activeMerchants / totalMerchants) * 100) : 0;
  const avgMerchantBalance = activeMerchants > 0 ? (metrics.totalMarketingBalance || 0) / activeMerchants : 0;

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-5 mb-6'>
      {/* 1-Snap Verification Throughput Card */}
      <div className='bg-white rounded-xl border border-gray-200 shadow-xs p-5'>
        <div className='flex items-center justify-between mb-3'>
          <div>
            <h4 className='text-sm font-bold text-gray-900'>1-Snap Verification Throughput</h4>
            <p className='text-xs text-gray-500'>Submission review completion and outcomes</p>
          </div>
          <span className='text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full'>
            {totalSnaps} total snaps
          </span>
        </div>

        {/* Multi-segment Progress Bar */}
        <div className='w-full h-3 bg-gray-100 rounded-full overflow-hidden flex my-3.5'>
          <div
            style={{ width: `${approvedPct}%` }}
            className='bg-emerald-500 h-full transition-all'
            title={`Approved: ${approvedPct}%`}
          />
          <div
            style={{ width: `${pendingPct}%` }}
            className='bg-amber-400 h-full transition-all'
            title={`Pending: ${pendingPct}%`}
          />
          <div
            style={{ width: `${rejectedPct}%` }}
            className='bg-rose-500 h-full transition-all'
            title={`Rejected: ${rejectedPct}%`}
          />
        </div>

        {/* Legend / Metrics breakdown */}
        <div className='grid grid-cols-3 gap-2 text-xs pt-1'>
          <div className='flex flex-col'>
            <span className='flex items-center gap-1.5 text-gray-500 font-medium'>
              <span className='w-2 h-2 rounded-full bg-emerald-500'></span> Approved
            </span>
            <span className='font-bold text-gray-900 mt-0.5'>
              {approvedPct}% ({approvedSnaps})
            </span>
          </div>

          <div className='flex flex-col'>
            <span className='flex items-center gap-1.5 text-gray-500 font-medium'>
              <span className='w-2 h-2 rounded-full bg-amber-400'></span> Pending
            </span>
            <span className='font-bold text-gray-900 mt-0.5'>
              {pendingPct}% ({pendingSnaps})
            </span>
          </div>

          <div className='flex flex-col'>
            <span className='flex items-center gap-1.5 text-gray-500 font-medium'>
              <span className='w-2 h-2 rounded-full bg-rose-500'></span> Rejected
            </span>
            <span className='font-bold text-gray-900 mt-0.5'>
              {rejectedPct}% ({rejectedSnaps})
            </span>
          </div>
        </div>

        <div className='text-xs text-gray-500 mt-3 pt-2.5 border-t border-gray-100 flex justify-between'>
          <span>Approved Snap Amount:</span>
          <strong className='text-emerald-600 font-bold'>
            {dashboardService.formatCurrency(metrics.approvedSnapAmount || 0)}
          </strong>
        </div>
      </div>

      {/* Merchant Balance Health Card */}
      <div className='bg-white rounded-xl border border-gray-200 shadow-xs p-5'>
        <div className='flex items-center justify-between mb-3'>
          <div>
            <h4 className='text-sm font-bold text-gray-900'>Merchant Funding Health</h4>
            <p className='text-xs text-gray-500'>Marketing balance liquidity & active coverage</p>
          </div>
          <span className='text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full'>
            {activeMerchantPct}% Active
          </span>
        </div>

        {/* Progress Bar for Active Merchants */}
        <div className='w-full h-3 bg-gray-100 rounded-full overflow-hidden my-3.5'>
          <div
            style={{ width: `${activeMerchantPct}%` }}
            className='bg-blue-600 h-full rounded-full transition-all'
          />
        </div>

        <div className='grid grid-cols-2 gap-3 text-xs pt-1'>
          <div>
            <span className='text-gray-500 font-medium'>Active Merchants:</span>
            <div className='text-sm font-bold text-gray-900 mt-0.5'>
              {activeMerchants} <span className='text-xs font-normal text-gray-400'>of {totalMerchants}</span>
            </div>
          </div>
          <div>
            <span className='text-gray-500 font-medium'>Avg. Marketing Balance:</span>
            <div className='text-sm font-bold text-gray-900 mt-0.5'>
              {dashboardService.formatCurrency(avgMerchantBalance)}
            </div>
          </div>
        </div>

        <div className='text-xs text-gray-500 mt-3 pt-2.5 border-t border-gray-100 flex justify-between'>
          <span>Total Marketing Pool:</span>
          <strong className='text-gray-900 font-bold'>
            {dashboardService.formatCurrency(metrics.totalMarketingBalance || 0)}
          </strong>
        </div>
      </div>
    </div>
  );
};

export default DashboardHealthCards;
